import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './ui/select';
import { Search, Filter, SlidersHorizontal, Map as MapIcon, List } from 'lucide-react';
import { JobCardUpdated as JobCard } from './JobCardUpdated';
import { JobMapView } from './JobMapView';
import { useData } from '../context/DataContext';
import { searchHistoryService } from '../services/searchHistoryService';
import { useApp } from '@/pages/providers/AppProvider';
import { normalizeTier } from '../utils/tier';
import type { JobCategory, Company } from '../lib/db-service';
import type { Job as DbJob } from '../lib/db-service';
import { Job } from '@/pages/providers/AppProvider';
import Loader from './ui/Loader';

const mockCategories: JobCategory[] = [
  { id: '1', name: 'Information Technology', description: 'IT and Technology Jobs', tier: 1, created_at: new Date(), updated_at: new Date() },
  { id: '2', name: 'Banking & Finance', description: 'Banking and Finance Jobs', tier: 1, created_at: new Date(), updated_at: new Date() },
  { id: '3', name: 'Healthcare', description: 'Healthcare and Medical Jobs', tier: 1, created_at: new Date(), updated_at: new Date() },
  { id: '4', name: 'Education', description: 'Education and Training Jobs', tier: 1, created_at: new Date(), updated_at: new Date() },
  { id: '5', name: 'Marketing & Sales', description: 'Marketing and Sales Jobs', tier: 1, created_at: new Date(), updated_at: new Date() }
];


interface JobListingsProps {
  onViewJob: (id: string) => void;
  onSaveJob: (id: string) => void;
  onApply?: (id: string) => void;
  savedJobs: string[];
  filter?: { type: string; value: string };
  isUserLoggedIn?: boolean;
  onLoginRequired?: () => void;
  onViewCompany?: (companyId: string) => void;
}

export function JobListings({ onViewJob, onSaveJob, onApply, savedJobs, filter, isUserLoggedIn = false, onLoginRequired, onViewCompany }: JobListingsProps) {
  const { jobs, loading, error, adminCities, adminIndustries } = useData();
  const { siteSettings, applications, currentUser } = useApp();
  const [categories, setCategories] = useState<JobCategory[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState<'list'>('list');

  const [filters, setFilters] = useState({
    category_id: '',
    location_id: '',
    tier: '',
    employment_type: '',
    experience_level: '',
    work_type: '', // remote, onsite, hybrid
    salary_range: '',
    is_internship: false,
    is_fresher_friendly: false,
    featured: false,
    search: ''
  });

  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Compute available locations matching HomePage logic
  const locations = React.useMemo(() => {
    const cityOnly = (val: any): string => {
      const raw = String(val || '').trim();
      if (!raw) return '';
      if (raw.includes(' - ')) {
        const after = raw.split(' - ')[1] || raw;
        const city = after.split(',')[0].trim();
        return city;
      }
      const toks = raw.split(/[,-]/).map(s => s.trim()).filter(Boolean);
      const skipWords = ['nepal', 'province', 'bagmati', 'gandaki', 'koshi', 'lumbini', 'madhesh', 'sudurpashchim'];
      for (const t of toks) {
        const k = t.toLowerCase();
        if (!skipWords.some(w => k.includes(w))) return t;
      }
      return toks[0] || raw;
    };

    const src = Array.isArray(adminCities) && adminCities.length ? adminCities : [];
    if (src.length) {
      const set = new Set<string>();
      src.forEach((name: string) => { const v = cityOnly(name); if (v) set.add(String(v)); });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }

    // Fallback to locations from jobs if adminCities is empty
    const set = new Set<string>();
    if (Array.isArray(jobs)) {
      jobs.forEach(j => {
        const v = cityOnly(j.location);
        if (v) set.add(v);
      });
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [adminCities, jobs]);

  const [locationFilterText, setLocationFilterText] = useState('');
  const [categoryFilterText, setCategoryFilterText] = useState('');

  useEffect(() => {
    if (Array.isArray(adminIndustries) && adminIndustries.length > 0) {
      const mapped = adminIndustries.map((name) => ({
        id: name,
        name,
        description: '',
        tier: 1,
        created_at: new Date(),
        updated_at: new Date()
      } as any));
      setCategories((mapped as JobCategory[]).sort((a, b) => a.name.localeCompare(b.name)));
    } else {
      setCategories([...mockCategories].sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [adminIndustries]);

  // Apply navigation filter when component receives filter prop
  useEffect(() => {
    // Reset filters to default whenever the 'filter' prop changes
    const defaultFilters = {
      category_id: '',
      location_id: '',
      tier: '',
      employment_type: '',
      experience_level: '',
      work_type: '',
      salary_range: '',
      is_internship: false,
      is_fresher_friendly: false,
      featured: false,
      search: ''
    };

    if (filter) {
      if (filter.type === 'featured') {
        setFilters({ ...defaultFilters, featured: true });
      } else if (filter.type === 'remote') {
        setFilters({ ...defaultFilters, work_type: 'remote' });
      } else if (filter.type === 'tier' && filter.value) {
        setFilters({ ...defaultFilters, tier: filter.value });
      } else {
        setFilters(defaultFilters);
      }
    } else {
      setFilters(defaultFilters);
    }
  }, [filter]);

  // Compute filtered jobs from DataContext
  const filteredJobs = React.useMemo(() => {
    const base = Array.isArray(jobs) ? jobs : [];
    const isExpired = (j: any) => {
      const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
      if (!raw) return false;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return false;
      return d.getTime() <= Date.now();
    };
    let result = base.filter(j => !isExpired(j));

    // Do not filter out jobs where the user has already applied
    // Instead, we will pass 'isApplied' prop to JobCard

    // Apply client-side filters
    if (searchQuery) {
      result = result.filter(job =>
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (Array.isArray(job.requirements) ? job.requirements.join(' ') : job.requirements || '').toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (filters.employment_type) {
      // Normalize filter value to match job data format (e.g., "full_time" -> "Full Time")
      const normalizedType = filters.employment_type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      result = result.filter(job => {
        const jobType = (job.type || '').trim();
        return jobType === normalizedType ||
          jobType.toLowerCase() === filters.employment_type.toLowerCase() ||
          jobType.toLowerCase() === normalizedType.toLowerCase();
      });
    }

    if (filters.category_id) {
      result = result.filter(job => {
        const jobCategory = (job.category || '').trim().toLowerCase();
        const filterCategory = (filters.category_id || '').trim().toLowerCase();
        // Match by exact value, name, or partial match
        return jobCategory === filterCategory ||
          jobCategory.includes(filterCategory) ||
          filterCategory.includes(jobCategory);
      });
    }

    if (filters.location_id) {
      result = result.filter(job => {
        const jobLocation = (job.location || '').trim().toLowerCase();
        const filterLocation = (filters.location_id || '').trim().toLowerCase();
        // Match by exact value or partial match (e.g., "Kathmandu Valley" matches "Kathmandu")
        return jobLocation === filterLocation ||
          jobLocation.includes(filterLocation) ||
          filterLocation.includes(jobLocation);
      });
    }

    if (filters.experience_level) {
      result = result.filter(job => {
        const jobExp = (job.experience || '').trim().toLowerCase();
        const filterExp = filters.experience_level.toLowerCase();
        // Match by value or by checking if job experience contains the filter
        return jobExp === filterExp ||
          jobExp.includes(filterExp) ||
          (filterExp === 'entry' && (jobExp.includes('entry') || jobExp.includes('junior') || jobExp.includes('fresher'))) ||
          (filterExp === 'mid' && jobExp.includes('mid')) ||
          (filterExp === 'senior' && jobExp.includes('senior')) ||
          (filterExp === 'executive' && (jobExp.includes('executive') || jobExp.includes('lead')));
      });
    }

    if (filters.work_type) {
      result = result.filter(job => {
        if (filters.work_type === 'remote') {
          return job.tags?.includes('Remote') ||
            job.title.toLowerCase().includes('remote') ||
            job.description.toLowerCase().includes('remote');
        }
        if (filters.work_type === 'hybrid') {
          return job.title.toLowerCase().includes('hybrid') ||
            job.description.toLowerCase().includes('hybrid');
        }
        if (filters.work_type === 'onsite') {
          return !job.tags?.includes('Remote') &&
            !job.title.toLowerCase().includes('remote') &&
            !job.title.toLowerCase().includes('hybrid') &&
            !job.description.toLowerCase().includes('remote') &&
            !job.description.toLowerCase().includes('hybrid');
        }
        return true;
      });
    }

    if (filters.is_internship) {
      result = result.filter(job =>
        job.title.toLowerCase().includes('intern') ||
        job.description.toLowerCase().includes('internship') ||
        job.tags?.some(tag => tag.toLowerCase().includes('intern'))
      );
    }

    if (filters.is_fresher_friendly) {
      result = result.filter(job =>
        job.experience === 'entry' ||
        job.title.toLowerCase().includes('junior') ||
        job.title.toLowerCase().includes('entry') ||
        job.description.toLowerCase().includes('fresher') ||
        job.description.toLowerCase().includes('no experience required')
      );
    }

    if (filters.featured) {
      result = result.filter(job => job.featured);
    }

    if (filters.tier && filters.tier !== 'latest') {
      result = result.filter(job => normalizeTier(job.tier) === normalizeTier(filters.tier));
    }

    // Sort jobs
    result.sort((a, b) => {
      switch (sortBy) {
        case 'salary':
          return (b.salary || '').localeCompare(a.salary || '');
        case 'relevance':
          return (b.id ? b.id.length : 0) - (a.id ? a.id.length : 0);
        case 'recent':
        default:
          return new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime();
      }
    });

    return result;
  }, [jobs, searchQuery, filters, sortBy, applications, currentUser]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    // Track search history for AI recommendations
    if (searchQuery.trim()) {
      searchHistoryService.addSearchHistory({
        query: searchQuery.trim(),
        filters: {
          category: filters.category_id,
          location: filters.location_id,
          jobType: filters.employment_type,
          experience: filters.experience_level
        },
        resultCount: filteredJobs.length,
        clickedJobs: []
      });
    }

    setCurrentPage(1);
  };

  const handleFilterChange = (key: string, value: string) => {
    let filterValue: any = value === 'all' ? '' : value;

    if (key === 'is_internship' || key === 'is_fresher_friendly' || key === 'featured') {
      filterValue = value === 'true';
    }

    setFilters(prev => ({ ...prev, [key]: filterValue }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      category_id: '',
      location_id: '',
      tier: '',
      employment_type: '',
      experience_level: '',
      work_type: '',
      salary_range: '',
      is_internship: false,
      is_fresher_friendly: false,
      featured: false,
      search: ''
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const formatSalary = (min?: number, max?: number, negotiable?: boolean) => {
    if (negotiable) return 'Negotiable';
    if (min && max) return `NPR ${min.toLocaleString()} - ${max.toLocaleString()}`;
    if (min) return `NPR ${min.toLocaleString()}+`;
    return 'Not specified';
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Convert job data to match JobCard interface
  const convertJobToJobCardFormat = (job: Job) => {
    // Map salary ranges to job tiers for better distribution
    const salaryString = job.salary || '';
    const salaryNumbers = salaryString.match(/\d+/g);
    const salaryMax = salaryNumbers ? Math.max(...salaryNumbers.map(Number)) : 0;
    let assignedTier = 'latest';

    if (salaryMax >= 150000) {
      assignedTier = 'megajob';
    } else if (salaryMax >= 100000) {
      assignedTier = 'premium';
    } else if (salaryMax >= 60000 || job.title.toLowerCase().includes('intern')) {
      assignedTier = 'prime';
    } else {
      assignedTier = 'latest';
    }

    // Override with featured jobs getting higher tiers
    if (job.title.toLowerCase().includes('featured') || job.title.toLowerCase().includes('senior') || job.title.toLowerCase().includes('lead')) {
      if (salaryMax >= 120000) {
        assignedTier = 'megajob';
      } else {
        assignedTier = 'premium';
      }
    }

    // Map company names from trusted brands
    const companyMap: { [key: string]: string } = {
      'google': 'Google',
      'microsoft': 'Microsoft',
      'netflix': 'Netflix',
      'meta': 'Meta',
      'apple': 'Apple',
      'amazon': 'Amazon',
      'spotify': 'Spotify',
      'slack': 'Slack',
      'tesla': 'Tesla',
      'pinterest': 'Pinterest',
      'adobe': 'Adobe',
      'airbnb': 'Airbnb',
      'salesforce': 'Salesforce',
      'zoom': 'Zoom',
      'uber': 'Uber'
    };

    const cleanCompanyName = job.title.toLowerCase();
    let companyName = 'TechCorp Nepal';

    // Try to map to known companies based on job title keywords
    for (const [key, value] of Object.entries(companyMap)) {
      if (cleanCompanyName.includes(key) || job.description.toLowerCase().includes(key)) {
        companyName = value;
        break;
      }
    }

    return {
      id: job.id || '',
      title: job.title,
      company: job.company || companyName,
      location: job.location,
      description: job.description,
      requirements: Array.isArray(job.requirements) ? job.requirements : [job.requirements || ''],
      postedDate: job.postedDate,
      publishedDate: job.publishedDate,
      deadline: job.deadline,
      salary: job.salary,
      type: job.type,
      tier: job.tier,
      category: job.category,
      experience: job.experience,
      tags: job.tags || [],
      featured: job.featured || false,
      source: job.source || 'online' as const,
      // Preserve available image fields so JobCard can prioritize covers
      logo: (job as any).logo || 'https://images.unsplash.com/photo-1549924231-f129b911e442?w=48&h=48&fit=crop&crop=center',
      companyLogo: (job as any).companyLogo || (job as any).company_logo || undefined,
      coverImageUrl: (job as any).coverImageUrl || (job as any).cover_image_url || null
    };
  };

  const getTierColor = (tier: string) => {
    const t = normalizeTier(tier) || 'latest';
    const cfg = siteSettings?.tierConfig?.[t];
    return cfg?.badgeClass || (
      t === 'megajob' ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white' :
        t === 'premium' ? 'bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white' :
          t === 'prime' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
            t === 'newspaper' ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' :
              'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
    );
  };

  // Deduplicate by company (show one job per company) and compute counts
  const dedupedJobsByCompany = React.useMemo(() => {
    const tierRank = (t: string | undefined) => {
      const n = normalizeTier(t);
      switch (n) {
        case 'megajob': return 5;
        case 'premium': return 4;
        case 'prime': return 3;
        case 'newspaper': return 2;
        default: return 1; // latest/unknown
      }
    };
    type Picked = { job: Job; count: number; allJobs: Job[] };
    const byCompany = new Map<string, Picked>();
    filteredJobs.forEach((job) => {
      const companyName = (job as any)?.company?.name || (job as any)?.company || '';
      const key = String(companyName).trim().toLowerCase();
      if (!key) return;
      const current: Picked | undefined = byCompany.get(key);
      if (!current) {
        byCompany.set(key, { job, count: 1, allJobs: [job] });
      } else {
        current.count += 1;
        current.allJobs.push(job);
        const a = current.job; const b = job;
        const rankA = tierRank((a as any).tier);
        const rankB = tierRank((b as any).tier);
        const dateA = new Date((a as any).publishedDate || (a as any).postedDate || 0).getTime();
        const dateB = new Date((b as any).publishedDate || (b as any).postedDate || 0).getTime();
        const shouldReplace = rankB > rankA || (rankB === rankA && dateB > dateA);
        if (shouldReplace) {
          byCompany.set(key, { job: b, count: current.count, allJobs: current.allJobs });
        }
      }
    });
    return Array.from(byCompany.values());
  }, [filteredJobs]);

  // Pagination on deduped list
  const totalPages = Math.ceil(dedupedJobsByCompany.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentJobs = dedupedJobsByCompany.slice(startIndex, startIndex + itemsPerPage);

  const employmentTypes = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'freelance', label: 'Freelance' }
  ];

  const jobTiers = [
    { value: 'mega_job', label: 'Mega Jobs' },
    { value: 'premium_job', label: 'Premium Job' },
    { value: 'prime_job', label: 'Prime Job' },
    { value: 'latest_job', label: 'Latest Job' },
    { value: 'newspaper_job', label: 'Newspaper Job' }
  ];


  if (loading) {
    return <Loader fullScreen text="Loading jobs..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {filter?.type === 'featured' ? 'Featured Jobs' :
              filter?.type === 'remote' ? 'Remote Jobs' :
                'Find Your Perfect Job'}
          </h1>
          <p className="text-lg text-gray-600">
            {filter?.type === 'featured' ? 'Discover the best featured job opportunities' :
              filter?.type === 'remote' ? 'Work from anywhere with these remote opportunities' :
                `Discover ${filteredJobs.length} opportunities waiting for you`}
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="bg-white shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative group">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-hover:text-blue-600 transition-colors duration-200" />
                <Input
                  placeholder="Search by job title, company, or keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 h-12 border-2 transition-all duration-200 hover:border-blue-400 focus:border-blue-600 focus:shadow-md"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Select value={filters.location_id || 'all'} onValueChange={(value) => handleFilterChange('location_id', value)}>
                  <SelectTrigger className="w-full sm:w-56 h-12 bg-white border-2 transition-all duration-200 hover:border-blue-400 hover:shadow-md">
                    <SelectValue placeholder="Any Location" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Search location..."
                        value={locationFilterText}
                        onChange={(e) => setLocationFilterText(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <SelectItem value="all">Any Location</SelectItem>
                    {locations
                      .filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase()))
                      .sort((a, b) => a.localeCompare(b))
                      .map((location, index) => (
                        <SelectItem key={`${location}-${index}`} value={location} textValue={location}>
                          {location}
                        </SelectItem>
                      ))}
                    {locations.filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase())).length === 0 && (
                      <SelectGroup>
                        <SelectLabel>No matches</SelectLabel>
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
                <Select value={filters.category_id || 'all'} onValueChange={(value) => handleFilterChange('category_id', value)}>
                  <SelectTrigger className="w-full sm:w-56 h-12 bg-white border-2 transition-all duration-200 hover:border-blue-400 hover:shadow-md">
                    <SelectValue placeholder="Any Industry" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    <div className="px-2 pb-2">
                      <Input
                        placeholder="Search industry..."
                        value={categoryFilterText}
                        onChange={(e) => setCategoryFilterText(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <SelectItem value="all">Any Industry</SelectItem>
                    {categories
                      .filter((c) => String(c?.name || '').toLowerCase().includes(categoryFilterText.toLowerCase()))
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((category, index) => (
                        <SelectItem
                          key={`${category.name}-${index}`}
                          value={category.id || category.name}
                          textValue={category.name}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    {categories.filter((c) => String(c?.name || '').toLowerCase().includes(categoryFilterText.toLowerCase())).length === 0 && (
                      <SelectGroup>
                        <SelectLabel>No matches</SelectLabel>
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button type="submit" className="h-12 bg-blue-600 hover:bg-blue-700 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 font-semibold">
                  <Search className="w-4 h-4 mr-2" />
                  Search Jobs
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="lg:hidden h-12 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {showFilters ? 'Hide' : 'Show'} Filters
                </Button>
              </div>
            </div>
          </div>
        </form>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* Filters Sidebar */}
          <div className={`lg:w-80 transition-all duration-300 ${showFilters ? 'block animate-slideInLeft' : 'hidden lg:block'}`}>
            <div className="bg-white shadow-xl p-6 sticky top-8 transition-all duration-300 hover:shadow-2xl">
              <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-gray-100">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-700" />
                  <h3 className="text-xl font-bold text-gray-900">Filters</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-sm font-semibold text-gray-600 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                >
                  Clear All
                </Button>
              </div>

              <div className="space-y-5">
                {/* Employment Type */}
                <div className="filter-group">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Employment Type</h4>
                    {filters.employment_type && filters.employment_type !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold animate-fadeIn">Active</span>
                    )}
                  </div>
                  <Select value={filters.employment_type || 'all'} onValueChange={(value) => handleFilterChange('employment_type', value)}>
                    <SelectTrigger className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-2 hover:border-blue-400">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {employmentTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div className="filter-group">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Location</h4>
                    {filters.location_id && filters.location_id !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold animate-fadeIn">Active</span>
                    )}
                  </div>
                  <Select value={filters.location_id || 'all'} onValueChange={(value) => handleFilterChange('location_id', value)}>
                    <SelectTrigger className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-2 hover:border-blue-400">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <div className="px-2 pb-2">
                        <Input
                          placeholder="Search location..."
                          value={locationFilterText}
                          onChange={(e) => setLocationFilterText(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <SelectItem value="all">All Locations</SelectItem>
                      {locations
                        .filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase()))
                        .sort((a, b) => a.localeCompare(b))
                        .map((location, index) => (
                          <SelectItem key={`${location}-${index}`} value={location} textValue={location}>
                            {location}
                          </SelectItem>
                        ))}
                      {locations.filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase())).length === 0 && (
                        <SelectGroup>
                          <SelectLabel>No matches</SelectLabel>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Industry */}
                <div className="filter-group">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Industry</h4>
                    {filters.category_id && filters.category_id !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold animate-fadeIn">Active</span>
                    )}
                  </div>
                  <Select value={filters.category_id || 'all'} onValueChange={(value) => handleFilterChange('category_id', value)}>
                    <SelectTrigger className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-2 hover:border-blue-400">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <div className="px-2 pb-2">
                        <Input
                          placeholder="Search industry..."
                          value={categoryFilterText}
                          onChange={(e) => setCategoryFilterText(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <SelectItem value="all">All Industries</SelectItem>
                      {categories
                        .filter((c) => String(c?.name || '').toLowerCase().includes(categoryFilterText.toLowerCase()))
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((category, index) => (
                          <SelectItem
                            key={`${category.name}-${index}`}
                            value={category.id || category.name}
                            textValue={category.name}
                          >
                            {category.name}
                          </SelectItem>
                        ))}
                      {categories.filter((c) => String(c?.name || '').toLowerCase().includes(categoryFilterText.toLowerCase())).length === 0 && (
                        <SelectGroup>
                          <SelectLabel>No matches</SelectLabel>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Experience Level */}
                <div className="filter-group">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Experience Level</h4>
                    {filters.experience_level && filters.experience_level !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold animate-fadeIn">Active</span>
                    )}
                  </div>
                  <Select value={filters.experience_level || 'all'} onValueChange={(value) => handleFilterChange('experience_level', value)}>
                    <SelectTrigger className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-2 hover:border-blue-400">
                      <SelectValue placeholder="Select experience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="entry">Entry Level</SelectItem>
                      <SelectItem value="mid">Mid Level</SelectItem>
                      <SelectItem value="senior">Senior Level</SelectItem>
                      <SelectItem value="executive">Executive Level</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Work Type */}
                <div className="filter-group">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wide">Work Type</h4>
                    {filters.work_type && filters.work_type !== 'all' && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-bold animate-fadeIn">Active</span>
                    )}
                  </div>
                  <Select value={filters.work_type || 'all'} onValueChange={(value) => handleFilterChange('work_type', value)}>
                    <SelectTrigger className="transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border-2 hover:border-blue-400">
                      <SelectValue placeholder="Select work type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="remote">Remote</SelectItem>
                      <SelectItem value="onsite">On-site</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Summary */}
              {(filters.employment_type || filters.location_id || filters.category_id || filters.experience_level || filters.work_type) &&
                (filters.employment_type !== 'all' || filters.location_id !== 'all' || filters.category_id !== 'all' ||
                  filters.experience_level !== 'all' || filters.work_type !== 'all') && (
                  <div className="mt-6 pt-4 border-t-2 border-gray-100 animate-fadeIn">
                    <p className="text-sm font-semibold text-gray-700 mb-2">Active Filters:</p>
                    <div className="flex flex-wrap gap-2">
                      {filters.employment_type && filters.employment_type !== 'all' && (
                        <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold shadow-sm">
                          {employmentTypes.find(t => t.value === filters.employment_type)?.label}
                        </span>
                      )}
                      {filters.location_id && filters.location_id !== 'all' && (
                        <span className="px-3 py-1 bg-green-600 text-white text-xs font-bold shadow-sm">
                          {filters.location_id}
                        </span>
                      )}
                      {filters.category_id && filters.category_id !== 'all' && (
                        <span className="px-3 py-1 bg-purple-600 text-white text-xs font-bold shadow-sm">
                          {categories.find(c => (c.id || c.name) === filters.category_id)?.name}
                        </span>
                      )}
                      {filters.experience_level && filters.experience_level !== 'all' && (
                        <span className="px-3 py-1 bg-orange-600 text-white text-xs font-bold shadow-sm">
                          {filters.experience_level.charAt(0).toUpperCase() + filters.experience_level.slice(1)} Level
                        </span>
                      )}
                      {filters.work_type && filters.work_type !== 'all' && (
                        <span className="px-3 py-1 bg-pink-600 text-white text-xs font-bold shadow-sm">
                          {filters.work_type.charAt(0).toUpperCase() + filters.work_type.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Job Results */}
          <div className="flex-1">

            {/* Sort and Results Count */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 bg-white p-4 shadow-sm transition-all duration-300 hover:shadow-md">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {dedupedJobsByCompany.length} {dedupedJobsByCompany.length === 1 ? 'Job' : 'Jobs'} Found
                  </p>
                  <p className="text-xs text-gray-500 font-medium">
                    Showing <span className="text-gray-900">{dedupedJobsByCompany.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + itemsPerPage, dedupedJobsByCompany.length)}</span> of <span className="text-blue-600 font-bold">{dedupedJobsByCompany.length}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <div className="flex items-center space-x-2 flex-1 sm:flex-initial">
                  <SlidersHorizontal className="w-4 h-4 text-gray-500" />
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full sm:w-44 bg-white border-2 transition-all duration-200 hover:border-blue-400 hover:shadow-md">
                      <SelectValue placeholder="Sort By" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="recent">Most Recent</SelectItem>
                      <SelectItem value="relevance">Most Relevant</SelectItem>
                      <SelectItem value="salary">Highest Salary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2 flex-1 sm:flex-initial">
                  <span className="text-sm text-gray-500 hidden sm:inline whitespace-nowrap">Show:</span>
                  <Select value={String(itemsPerPage)} onValueChange={(val) => {
                    setItemsPerPage(Number(val));
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="w-[80px] bg-white border-2 transition-all duration-200 hover:border-blue-400 hover:shadow-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Job Cards Grid or Map View */}
            {filteredJobs.length === 0 ? (
              <div className="text-center py-16 bg-white shadow-sm">
                <div className="max-w-md mx-auto">
                  <div className="text-gray-400 mb-4">
                    <Search className="w-16 h-16 mx-auto" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No jobs found</h3>
                  <p className="text-gray-600 mb-6">
                    We couldn't find any jobs matching your criteria. Try adjusting your filters or search terms.
                  </p>
                  <Button onClick={clearFilters} variant="outline">
                    Clear All Filters
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 job-grid mb-3 max-w-none">
                {currentJobs.map(({ job, count, allJobs }, index) => {
                  let convertedJob: any = null;
                  try {
                    convertedJob = convertJobToJobCardFormat(job);
                    convertedJob.companyJobCount = count;
                  } catch {
                    return null;
                  }
                  if (!convertedJob || !convertedJob.id) return null;
                  return (
                    <div key={convertedJob.id} className="job-card-container">
                      <JobCard
                        job={convertedJob}
                        onViewJob={onViewJob}
                        onApply={onApply}
                        tierColor={getTierColor(convertedJob.tier)}
                        tierBgColor="border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white"
                        onSaveJob={onSaveJob}
                        onLoginRequired={onLoginRequired}
                        isSaved={savedJobs.includes(convertedJob.id || '')}
                        isApplied={currentUser?.type === 'jobseeker' && applications.some(a => String(a.jobId) === String(convertedJob.id))}
                        isUserLoggedIn={isUserLoggedIn}
                        onViewCompany={onViewCompany}
                        imageMode="job"
                        allCompanyJobs={allJobs}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex flex-col sm:flex-row justify-between items-center bg-white p-6 shadow-sm border border-gray-100 rounded-xl gap-6">
                <div className="text-sm text-gray-500 font-medium">
                  Showing <span className="text-gray-900 font-bold">{startIndex + 1}</span> to <span className="text-gray-900 font-bold">{Math.min(startIndex + itemsPerPage, dedupedJobsByCompany.length)}</span> of <span className="text-gray-900 font-bold">{dedupedJobsByCompany.length}</span> jobs
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentPage(prev => Math.max(prev - 1, 1));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === 1}
                    className="h-10 px-4 font-semibold transition-all duration-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 disabled:opacity-50"
                  >
                    Previous
                  </Button>

                  <div className="hidden md:flex items-center space-x-1">
                    {(() => {
                      const pages = [];
                      const delta = 2;
                      const left = currentPage - delta;
                      const right = currentPage + delta + 1;
                      const range = [];
                      const rangeWithDots = [];
                      let l;

                      for (let i = 1; i <= totalPages; i++) {
                        if (i === 1 || i === totalPages || (i >= left && i < right)) {
                          range.push(i);
                        }
                      }

                      for (let i of range) {
                        if (l) {
                          if (i - l === 2) {
                            rangeWithDots.push(l + 1);
                          } else if (i - l !== 1) {
                            rangeWithDots.push('...');
                          }
                        }
                        rangeWithDots.push(i);
                        l = i;
                      }

                      return rangeWithDots.map((page, index) => (
                        <Button
                          key={index}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => {
                            if (typeof page === 'number') {
                              setCurrentPage(page);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                          }}
                          disabled={page === '...'}
                          className={`min-w-[40px] h-10 font-bold transition-all duration-200 ${currentPage === page
                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md transform scale-110 z-10'
                            : 'hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'
                            } ${page === '...' ? 'cursor-default border-none pointer-events-none' : ''}`}
                        >
                          {page}
                        </Button>
                      ));
                    })()}
                  </div>

                  <div className="md:hidden">
                    <span className="text-sm font-bold bg-blue-50 text-blue-600 px-4 py-2 rounded-lg">
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCurrentPage(prev => Math.min(prev + 1, totalPages));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    disabled={currentPage === totalPages}
                    className="h-10 px-4 font-semibold transition-all duration-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 disabled:opacity-50"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
